import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DashboardWidgetCard } from '@/components/dashboard/DashboardWidgetCard';

export type InquiryPipelineChartRow = {
  monthLabel: string;
  cumulativeInquiries: number;
  /** Budget in account currency units (cumulative). */
  cumulativeBudget: number;
};

export function InquiryPipelineChart({ data }: { data: InquiryPipelineChartRow[] }) {
  const { t } = useTranslation();
  const chartData = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        cumulativeBudgetK: row.cumulativeBudget / 1000,
      })),
    [data]
  );

  return (
    <DashboardWidgetCard
      className="min-h-[320px]"
      title={t('dashboard.inquirer.widgets.pipeline.title')}
      description={t('dashboard.inquirer.widgets.pipeline.subtitle')}
      contentClassName="h-[260px] px-2 pb-4 pt-0"
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} className="text-muted-foreground" />
          <YAxis
            yAxisId="left"
            orientation="left"
            tick={{ fontSize: 12 }}
            className="text-muted-foreground"
            allowDecimals={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12 }}
            className="text-muted-foreground"
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
            }}
            formatter={(value: number | string, name: string) => {
              if (name === t('dashboard.inquirer.widgets.pipeline.seriesBudget')) {
                return [typeof value === 'number' ? value.toFixed(1) : value, name];
              }
              return [value, name];
            }}
          />
          <Legend />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="cumulativeInquiries"
            name={t('dashboard.inquirer.widgets.pipeline.seriesInquiries')}
            stroke="var(--brand-secondary)"
            fill="var(--tint-blue)"
            fillOpacity={0.35}
            strokeWidth={2}
          />
          <Area
            yAxisId="right"
            type="monotone"
            dataKey="cumulativeBudgetK"
            name={t('dashboard.inquirer.widgets.pipeline.seriesBudget')}
            stroke="var(--success)"
            fill="var(--success)"
            fillOpacity={0.08}
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </DashboardWidgetCard>
  );
}
